import { useState } from "react";
import { Stack, DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, PrimaryButton, Spinner, SpinnerSize, TextField, DefaultButton } from "@fluentui/react";
import { getLogmon, getLogmonCsv } from "../../../api/admin/api_admin";
import { LogmonLog, LogmonResponse } from "../../../api/admin/models_admin";
import { AdminLabel, CellContent, ErrorDialog, formatDateTime, calcMinWidth } from "../components";

const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getThisMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
        start: formatDate(firstDay),
        end: formatDate(lastDay)
    };
};

function addMonths(date: Date, months: number) {
  const oldDate = date;
  // oldDateが月末かどうかを判定
  const isLastDayOfMonth = oldDate.getDate() === new Date(oldDate.getFullYear(), oldDate.getMonth() + 1, 0).getDate();
  // oldDateの日付を取得
  const oldDay = oldDate.getDate();
  // oldDateの月を取得
  const oldMonth = oldDate.getMonth();
  // oldDateの月の1日を取得
  const oldFirstDate = new Date(oldDate.getFullYear(), oldMonth, 1);
  // newDateの月を取得
  const newMonth = oldMonth + months;
  // newDateの月の1日を取得
  const newFirstDate = (new Date(oldFirstDate)).setMonth(newMonth);
  // newDateの月末日を取得
  const newLastDate = new Date(new Date(newFirstDate).getFullYear(), new Date(newFirstDate).getMonth() + 1, 0);
  // newDateの月末日付を取得
  const newLastDay = newLastDate.getDate();

  // 月末条件分岐
  if (isLastDayOfMonth){
    // 月末の場合
    // newDateの月末日付を設定
    return newLastDate;
  } else {
    // 月末でない場合
    // oldDayとnewLastDayを比較
    if (oldDay <= newLastDay) {
      // oldDayがnewLastDay以下の場合
      // months月後のoldDayを設定
      const newDate = new Date(oldDate);
      newDate.setMonth(newMonth);
      return newDate;
    } else {
      // oldDayがnewLastDayより大きい場合
      // newLastDateを設定
      return newLastDate;
    }
  }
}

export const Logmon = () => {
    const fixedUserIdWidth = 150;
    const fixedUserNameWidth = 180;
    const thisMonth = getThisMonthDates();
    const [logmonData, setLogmonData] = useState<LogmonResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [startdate, setStartdate] = useState<string>(thisMonth.start);
    const [enddate, setEnddate] = useState<string>(thisMonth.end);
    const [hasSearched, setHasSearched] = useState<boolean>(false);

    const fetchLogmon = async (start?: string, end?: string) => {
        setLoading(true);
        setError(null);
        setHasSearched(true);
        try {
            const data = await getLogmon(start, end);
            if ("error" in data) {
                setError(data.error);
            } else {
                setLogmonData(data);
            }
        } catch (err) {
            setError("ログモニタリング情報の取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        fetchLogmon(startdate, enddate);
    };

    const handleDownload = async () => {
        setDownloading(true);
        setError(null);
        try {
            const data = await getLogmonCsv(startdate, enddate)
            if (data !== null) {
                setError(data.error);
            }
        } catch (err) {
            setError("ログモニタリング情報の取得中にエラーが発生しました");
        } finally {
            setDownloading(false);
        }
    }

    const setOneMonthAgo = () => {
        if (startdate) {
            // 開始日の１ヶ月前の日付を取得
            let startDate = new Date(startdate);
            startDate = addMonths(startDate, -1);
            setStartdate(formatDate(startDate))
        }
        if (enddate) {
            // 終了日の１ヶ月前の日付を取得
            let endDate = new Date(enddate);
            endDate = addMonths(endDate, -1);
            setEnddate(formatDate(endDate))
        }
    };

    const setThisMonth = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setStartdate(formatDate(firstDay));
        setEnddate(formatDate(lastDay));
    };

    const setOneMonthLater = () => {
        if (startdate) {
            // 開始日の１ヶ月前の日付を取得
            let startDate = new Date(startdate);
            startDate = addMonths(startDate, 1);
            setStartdate(formatDate(startDate))
        }
        if (enddate) {
            // 終了日の１ヶ月前の日付を取得
            let endDate = new Date(enddate);
            endDate = addMonths(endDate, 1);
            setEnddate(formatDate(endDate))
        }
    };

    const clearDates = () => {
        setStartdate("");
        setEnddate("");
    };

    const buildColumns = (): IColumn[] => {
        const baseColumns: IColumn[] = [
            {
                key: 'userid',
                name: 'ユーザーID',
                fieldName: 'userid',
                minWidth: fixedUserIdWidth,
                maxWidth: fixedUserIdWidth,
                isResizable: false,
                onRender: (log: LogmonLog) => (
                    <CellContent>{log.userid}</CellContent>
                )
            },
            {
                key: 'username',
                name: 'ユーザー名',
                fieldName: 'username',
                minWidth: fixedUserNameWidth,
                maxWidth: fixedUserNameWidth,
                isResizable: false,
                onRender: (log: LogmonLog) => (
                    <CellContent>{log.username || ""}</CellContent>
                )
            },
            {
                key: 'call_count',
                name: 'メッセージ数',
                fieldName: 'call_count',
                minWidth: calcMinWidth('メッセージ数'),
                maxWidth: 120,
                isResizable: true,
                onRender: (log: LogmonLog) => (
                    <CellContent>
                        <div style={{ textAlign: 'right', width: '100%' }}>{log.call_count}</div>
                    </CellContent>
                )
            }
        ];

        // モデルごとの列を追加
        if (logmonData?.models) {
            logmonData.models.forEach(model => {
                baseColumns.push({
                    key: model,
                    name: model,
                    minWidth: calcMinWidth(model),
                    maxWidth: Math.max(150, calcMinWidth(model)),
                    isResizable: true,
                    onRender: (log: LogmonLog) => (
                        <CellContent>
                            <div style={{ textAlign: 'right', width: '100%' }}>
                                {log.model_usages[model] || 0}
                            </div>
                        </CellContent>
                    )
                });
            });
        }

        // サービス毎の列を追加
        if (logmonData?.services) {
            logmonData.services.forEach(service => {
                baseColumns.push({
                    key: service.name,
                    name: `${service.name}`,
                    minWidth: calcMinWidth(service.name),
                    isResizable: true,
                    onRender: (log: LogmonLog) => (
                        <CellContent>
                            <div style={{ textAlign: 'right', width: '100%' }}>
                                {`${log.service_usages[service.name] || 0} ${service.unit_name}`}
                            </div>
                        </CellContent>
                    )
                })
            })
        }

        baseColumns.push({
            key: 'operation_datetime',
            name: 'オンラインユーザー(最終操作時間)',
            fieldName: 'operation_datetime',
            minWidth: calcMinWidth('オンラインユーザー(最終操作時間)'),
            maxWidth: 300,
            isResizable: true,
            onRender: (log: LogmonLog) => (
                <CellContent>{formatDateTime(log.operation_datetime)}</CellContent>
            )
        });

        return baseColumns;
    };

    const columns = buildColumns();
    const logs = logmonData?.loglist || [];

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack tokens={{ childrenGap: 10 }}>
                <AdminLabel text="ユーザー利用一覧" />
                
                <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                    <Stack tokens={{ childrenGap: 5 }}>
                        <label>From:</label>
                        <TextField
                            type="date"
                            value={startdate}
                            onChange={(_, newValue) => setStartdate(newValue || "")}
                            styles={{ root: { width: 150 } }}
                            autoComplete="off"
                        />
                    </Stack>
                    
                    <Stack tokens={{ childrenGap: 5 }}>
                        <label>To:</label>
                        <TextField
                            type="date"
                            value={enddate}
                            onChange={(_, newValue) => setEnddate(newValue || "")}
                            styles={{ root: { width: 150 } }}
                            autoComplete="off"
                        />
                    </Stack>

                    <DefaultButton text="1ヶ月前" onClick={setOneMonthAgo} />
                    <DefaultButton text="今月" onClick={setThisMonth} />
                    <DefaultButton text="1ヶ月先" onClick={setOneMonthLater} />
                    <DefaultButton text="全期間" onClick={clearDates} />
                    <PrimaryButton text="照会" onClick={handleSearch} style={{marginLeft: "100px"}} disabled={loading||downloading} />
                    <PrimaryButton text="CSV出力" onClick={handleDownload} disabled={loading||downloading} />
                </Stack>

                {loading && (
                    <Stack horizontalAlign="center" style={{ padding: '20px' }}>
                        <Spinner size={SpinnerSize.large} label="読み込み中..." />
                    </Stack>
                )}

                {!loading && hasSearched && (
                    <DetailsList
                        items={[...logs]}
                        columns={columns}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        isHeaderVisible={true}
                        onShouldVirtualize={() => false}
                        styles={{
                            root: {
                                maxHeight: "64vh",
                                overflowY: "auto",
                                overflowX: "auto"
                            },
                            headerWrapper: {
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                                background: "#ffffff",
                                selectors: {
                                    ".ms-DetailsHeader-cell:nth-child(1)": {
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 5,
                                        background: "#ffffff"
                                    },
                                    ".ms-DetailsHeader-cell:nth-child(2)": {
                                        position: "sticky",
                                        left: fixedUserIdWidth,
                                        zIndex: 5,
                                        background: "#ffffff",
                                        boxShadow: "2px 0 0 #edebe9"
                                    }
                                }
                            },
                            contentWrapper: {
                                selectors: {
                                    ".ms-DetailsRow-cell:nth-child(1)": {
                                        position: "sticky",
                                        left: 0,
                                        background: "#ffffff"
                                    },
                                    ".ms-DetailsRow-cell:nth-child(2)": {
                                        position: "sticky",
                                        left: fixedUserIdWidth,
                                        background: "#ffffff",
                                        boxShadow: "2px 0 0 #edebe9"
                                    },
                                    ".ms-DetailsRow-cell:nth-child(1), .ms-DetailsRow-cell:nth-child(2)": {
                                        zIndex: 2
                                    }
                                }
                            }
                        }}
                    />
                )}
            </Stack>
        </Stack>
    );
}
